import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Trash2, RefreshCw, Plug, Check, ChevronRight, ExternalLink, Sparkles, Send, X, Edit3, Search } from "lucide-react";

const PROVIDERS = [
  {
    key: "gmail",
    label: "Gmail",
    appPasswordUrl: "https://myaccount.google.com/apppasswords",
    steps: [
      { text: "Enable 2-Step Verification", url: "https://myaccount.google.com/security", linkLabel: "Open Google security" },
      { text: "Generate an app password and name it 'Peak CRM'", url: "https://myaccount.google.com/apppasswords", linkLabel: "Open Gmail app passwords" },
      { text: "Copy the 16-char password and paste below (spaces optional)" },
    ],
  },
  {
    key: "yahoo",
    label: "Yahoo",
    appPasswordUrl: "https://login.yahoo.com/account/security/app-passwords",
    steps: [
      { text: "Turn on 2-Step Verification in Yahoo security", url: "https://login.yahoo.com/account/security", linkLabel: "Open Yahoo security" },
      { text: "Generate an app password", url: "https://login.yahoo.com/account/security/app-passwords", linkLabel: "Open Yahoo app passwords" },
      { text: "Copy the password and paste below" },
    ],
  },
  {
    key: "icloud",
    label: "iCloud",
    appPasswordUrl: "https://appleid.apple.com/account/manage",
    steps: [
      { text: "Sign in to Apple ID and turn on 2FA", url: "https://appleid.apple.com/account/manage", linkLabel: "Open Apple ID" },
      { text: "Under Sign-In and Security → 'App-Specific Passwords' → Generate", url: "https://appleid.apple.com/account/manage", linkLabel: "Open Apple ID" },
      { text: "Name it 'Peak CRM', copy and paste below" },
    ],
  },
];

export default function Inbox() {
  const [accounts, setAccounts] = useState([]);
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [provider, setProvider] = useState("gmail");
  const [form, setForm] = useState({ email: "", app_password: "" });
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null); // {subject, body, to, contact_name}
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [filterAccountId, setFilterAccountId] = useState("all"); // 'all' | <account.id>
  const [composing, setComposing] = useState(false);
  const [compose, setCompose] = useState({ account_id: "", to_email: "", subject: "", body: "" });
  const [composeSending, setComposeSending] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiGoal, setAiGoal] = useState("");
  const [aiSearch, setAiSearch] = useState("");
  const [aiContacts, setAiContacts] = useState([]);
  const [aiSelected, setAiSelected] = useState(null); // {id, first_name, last_name, email}
  const [aiDrafting, setAiDrafting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkedRef = useRef(false);

  const loadAccounts = async () => {
    const { data } = await api.get("/inbox/accounts");
    setAccounts(data);
  };
  const loadThreads = async (accId = filterAccountId) => {
    const params = accId && accId !== "all" ? { account_id: accId } : {};
    const { data } = await api.get("/inbox/threads", { params });
    setThreads(data);
    // Re-sync selectedThread with fresh data from server
    setSelectedThread((prev) => {
      if (!prev) return null;
      return data.find((t) => t.thread_key === prev.thread_key) || null;
    });
  };
  useEffect(() => { loadAccounts(); loadThreads("all"); }, []);

  // Deep link from Dashboard Priority Inbox: ?thread=<key>&ai=1
  useEffect(() => {
    if (deepLinkedRef.current) return;
    if (!threads.length) return;
    const wantThread = searchParams.get("thread");
    if (!wantThread) return;
    const match = threads.find((t) => t.thread_key === wantThread);
    if (!match) return;
    deepLinkedRef.current = true;
    // selectThread handles mark-read + state update
    selectThread(match);
    if (searchParams.get("ai") === "1" && match.latest_inbound_id) {
      draftReply(match.latest_inbound_id);
    }
    // Clean the URL so revisits don't re-trigger AI drafting
    const next = new URLSearchParams(searchParams);
    next.delete("thread"); next.delete("ai");
    setSearchParams(next, { replace: true });
  }, [threads]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchFilter = (val) => {
    setFilterAccountId(val);
    setSelectedThread(null);
    setDraft(null);
    loadThreads(val);
  };

  const openCompose = () => {
    const defaultAcct = filterAccountId !== "all"
      ? accounts.find((a) => a.id === filterAccountId)?.id
      : accounts[0]?.id;
    setCompose({ account_id: defaultAcct || "", to_email: "", subject: "", body: "" });
    setComposing(true);
  };
  const sendCompose = async () => {
    if (!compose.account_id || !compose.to_email.trim() || !compose.subject.trim() || !compose.body.trim()) {
      toast.error("From, To, Subject and Body are all required");
      return;
    }
    setComposeSending(true);
    try {
      const { data } = await api.post("/inbox/compose", compose);
      if (data.sent_folder) toast.success(`Sent to ${compose.to_email} — copied to ${data.sent_folder}`);
      else if (data.sent_folder_error) toast.success(`Sent to ${compose.to_email} · Sent-folder copy skipped (${data.sent_folder_error})`);
      else toast.success(`Sent to ${compose.to_email}`);
      setComposing(false);
      setCompose({ account_id: "", to_email: "", subject: "", body: "" });
      setAiOpen(false); setAiGoal(""); setAiSearch(""); setAiSelected(null); setAiContacts([]);
      loadThreads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setComposeSending(false); }
  };

  // Debounced contact search for AI-draft picker
  useEffect(() => {
    if (!aiOpen) return;
    const q = aiSearch.trim();
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/contacts${q ? `?q=${encodeURIComponent(q)}` : ""}`);
        setAiContacts((data || []).slice(0, 8));
      } catch (_e) { /* silent */ }
    }, 220);
    return () => clearTimeout(t);
  }, [aiSearch, aiOpen]);

  const pickContact = (c) => {
    setAiSelected(c);
    setAiSearch(`${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email);
  };
  const aiDraft = async () => {
    if (!aiGoal.trim()) { toast.error("Describe the goal in one line"); return; }
    // Resolve target: prefer selected contact, else the To field if it's an email
    const to_email = aiSelected?.email || (compose.to_email.includes("@") ? compose.to_email : null);
    if (!aiSelected && !to_email) { toast.error("Pick a contact or type an email in the 'To' field first"); return; }
    setAiDrafting(true);
    try {
      const { data } = await api.post("/inbox/compose/ai-draft", {
        goal: aiGoal,
        contact_id: aiSelected?.id || null,
        to_email: to_email,
      });
      setCompose((c) => ({
        ...c,
        to_email: data.to_email || c.to_email,
        subject: data.subject || c.subject,
        body: data.body || c.body,
      }));
      toast.success(data.crm_enriched ? `Drafted with CRM history for ${data.contact_name || data.to_email}` : "Drafted (no CRM record — cold outreach)");
      setAiOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiDrafting(false); }
  };

  const testConn = async () => {
    setBusy(true);
    try {
      await api.post("/inbox/accounts/test", { provider, ...form });
      toast.success("Connection successful");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const connect = async () => {
    setBusy(true);
    try {
      await api.post("/inbox/accounts", { provider, ...form });
      toast.success("Account connected — syncing recent messages");
      setConnecting(false); setForm({ email: "", app_password: "" });
      setTimeout(() => { loadAccounts(); loadThreads(); }, 2500);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const sync = async (a) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/inbox/accounts/${a.id}/sync`);
      toast.success(`Synced ${data.fetched} new message${data.fetched === 1 ? "" : "s"}`);
      loadAccounts(); loadThreads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (a) => {
    if (!confirm(`Disconnect ${a.email}?`)) return;
    try {
      await api.delete(`/inbox/accounts/${a.id}`);
      loadAccounts(); loadThreads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const replyTargetId = selectedThread?.latest_inbound_id;

  const draftReply = async (targetId) => {
    const mid = targetId || replyTargetId;
    if (!mid) return;
    setDrafting(true);
    try {
      const { data } = await api.post(`/inbox/messages/${mid}/ai-reply`);
      setDraft(data);
      if (data.contact_name) toast.success(`Draft ready — enriched with CRM history for ${data.contact_name}`);
      else toast.success("Draft ready");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDrafting(false); }
  };
  const sendReply = async () => {
    if (!draft || !replyTargetId) return;
    if (!draft.subject.trim() || !draft.body.trim()) { toast.error("Subject and body required"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/inbox/messages/${replyTargetId}/send-reply`, { subject: draft.subject, body: draft.body });
      if (data.sent_folder) toast.success(`Reply sent to ${draft.to} — copied to ${data.sent_folder}`);
      else if (data.sent_folder_error) toast.success(`Reply sent to ${draft.to} · Sent-folder copy skipped (${data.sent_folder_error})`);
      else toast.success(`Reply sent to ${draft.to}`);
      setDraft(null);
      loadThreads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  const selectThread = async (t) => {
    setSelectedThread(t);
    setDraft(null);
    // Mark unread inbound messages in this thread as read.
    if (t?.thread_key && (t.unread_count || 0) > 0) {
      try {
        await api.post(`/inbox/threads/${encodeURIComponent(t.thread_key)}/mark-read`);
        // Optimistically zero the unread count on this thread
        setThreads((prev) => prev.map((x) => x.thread_key === t.thread_key ? { ...x, unread_count: 0 } : x));
        setSelectedThread((prev) => prev && prev.thread_key === t.thread_key ? { ...prev, unread_count: 0 } : prev);
        // Notify AppLayout to refresh the sidebar unread badge
        window.dispatchEvent(new Event("peak:inbox-read"));
      } catch (_e) { /* silent */ }
    }
  };

  const providerDef = PROVIDERS.find((p) => p.key === provider);

  return (
    <div className="p-6 md:p-10" data-testid="inbox-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="section-label">Received mail</div>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter uppercase mt-1">Inbox</h1>
          <p className="text-[var(--peak-muted)] mt-1 text-sm">Read replies from your Gmail / Yahoo / iCloud right inside Peak.</p>
        </div>
        <div className="flex gap-2">
          {accounts.length > 0 && (
            <button data-testid="compose-btn" onClick={openCompose} className="bg-[var(--peak-blue-deep)] hover:bg-black text-white font-bold uppercase tracking-widest px-4 py-3 inline-flex items-center gap-2">
              <Edit3 className="w-4 h-4" /> Compose
            </button>
          )}
          <button data-testid="connect-account-btn" onClick={() => setConnecting(true)} className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase tracking-widest px-6 py-3 inline-flex items-center gap-2">
            <Plug className="w-4 h-4" /> Connect email
          </button>
        </div>
      </div>

      {/* Connected accounts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="accounts-list">
        {accounts.length === 0 && !connecting && (
          <div className="col-span-full brutalist-card p-10 text-center">
            <Mail className="w-6 h-6 mx-auto text-[var(--peak-blue-deep)]" />
            <div className="font-heading font-bold text-2xl mt-2">No accounts connected yet</div>
            <div className="section-label mt-1">Click "Connect email" to add Gmail, Yahoo, or iCloud via app password.</div>
          </div>
        )}
        {accounts.map((a) => (
          <div key={a.id} className="brutalist-card p-4" data-testid={`account-${a.id}`}>
            <div className="flex items-center justify-between">
              <div className="section-label">{a.provider} · {a.status}</div>
              <div className="flex gap-1">
                <button onClick={() => sync(a)} data-testid={`sync-${a.id}`} className="p-1.5 border border-[var(--peak-border)] hover:border-[var(--peak-ink)]"><RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /></button>
                <button onClick={() => remove(a)} data-testid={`disconnect-${a.id}`} className="p-1.5 border border-[var(--peak-border)] hover:border-[#E50000] hover:text-[#E50000]"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="font-heading font-bold text-lg tracking-tight mt-1 truncate">{a.email}</div>
            {a.last_synced_at && <div className="section-label mt-1">Last sync · {new Date(a.last_synced_at).toLocaleString()}</div>}
            {a.last_error && <div className="text-xs text-[#E50000] mt-1">{a.last_error}</div>}
          </div>
        ))}
      </div>

      {/* Connect form */}
      {connecting && (
        <div className="brutalist-card p-6 mb-6" data-testid="connect-form">
          <div className="section-label mb-3">Connect a new email account</div>
          <div className="flex gap-2 mb-4 flex-wrap">
            {PROVIDERS.map((p) => (
              <button key={p.key} data-testid={`provider-${p.key}`} onClick={() => setProvider(p.key)} className={`px-4 py-2 text-sm font-bold uppercase tracking-widest border ${provider === p.key ? "bg-[var(--peak-ink)] text-white border-[var(--peak-ink)]" : "border-[var(--peak-border)] hover:border-[var(--peak-ink)]"}`}>{p.label}</button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="section-label">Email address</label>
              <input data-testid="conn-email" className="brutalist-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@gmail.com" />
              <label className="section-label mt-4">App password</label>
              <input data-testid="conn-password" className="brutalist-input" type="password" value={form.app_password} onChange={(e) => setForm({ ...form, app_password: e.target.value })} placeholder="16-character app password" />
              <div className="mt-6 flex gap-2 flex-wrap">
                <button onClick={testConn} disabled={busy || !form.email || !form.app_password} data-testid="test-btn" className="border-2 border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase text-xs tracking-widest px-4 py-2 disabled:opacity-60 inline-flex items-center gap-1"><Check className="w-3 h-3" /> Test connection</button>
                <button onClick={connect} disabled={busy || !form.email || !form.app_password} data-testid="connect-btn" className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest px-4 py-2 disabled:opacity-60">{busy ? "Connecting…" : "Connect"}</button>
                <button onClick={() => setConnecting(false)} className="border border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase text-xs tracking-widest px-4 py-2">Cancel</button>
              </div>
            </div>
            <div className="bg-[var(--peak-surface-alt)] border border-[var(--peak-border)] p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="section-label">How to get an app password for {providerDef.label}</div>
                <a href={providerDef.appPasswordUrl} target="_blank" rel="noopener noreferrer" data-testid={`open-${providerDef.key}-settings`} className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest bg-[var(--peak-ink)] text-white hover:bg-[var(--peak-accent)] px-3 py-1.5">
                  Open {providerDef.label} settings <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <ol className="text-sm space-y-2 list-decimal list-inside">
                {providerDef.steps.map((s, i) => (
                  <li key={i}>
                    {s.text}
                    {s.url && (
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="ml-1 inline-flex items-center gap-0.5 text-[var(--peak-blue-deep)] hover:text-[var(--peak-accent)] underline underline-offset-2 font-semibold">
                        {s.linkLabel} <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Thread list + conversation view */}
      {accounts.length > 0 && (
        <>
          {accounts.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-4" data-testid="account-filter-tabs">
              <button
                onClick={() => switchFilter("all")}
                data-testid="filter-all"
                className={`text-xs font-bold uppercase tracking-widest px-3 py-1.5 border-2 ${filterAccountId === "all" ? "bg-[var(--peak-ink)] text-white border-[var(--peak-ink)]" : "border-[var(--peak-border)] text-[var(--peak-muted)] hover:border-[var(--peak-ink)] hover:text-[var(--peak-ink)]"}`}
              >
                All ({accounts.length})
              </button>
              {accounts.map((a) => (
                <button
                  key={a.id}
                  onClick={() => switchFilter(a.id)}
                  data-testid={`filter-account-${a.id}`}
                  className={`text-xs font-bold uppercase tracking-widest px-3 py-1.5 border-2 truncate max-w-[280px] ${filterAccountId === a.id ? "bg-[var(--peak-ink)] text-white border-[var(--peak-ink)]" : "border-[var(--peak-border)] text-[var(--peak-muted)] hover:border-[var(--peak-ink)] hover:text-[var(--peak-ink)]"}`}
                  title={a.email}
                >
                  <span className="text-[var(--peak-accent)] mr-1">{a.provider}</span>{a.email}
                </button>
              ))}
            </div>
          )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="brutalist-card p-0 lg:col-span-1 max-h-[600px] overflow-y-auto tick-scroll" data-testid="threads-list">
            {threads.length === 0 && <div className="p-6 text-sm text-[var(--peak-muted)]">No conversations yet. Try &quot;Sync&quot; on your account card.</div>}
            {threads.map((t) => {
              const active = selectedThread?.thread_key === t.thread_key;
              const unread = t.unread_count || 0;
              const otherName = t.participants[0] || "(unknown)";
              // Which of our connected accounts saw this thread? (dedupe across messages)
              const acctEmails = Array.from(new Set(
                (t.messages || []).map((m) => m.account_email).filter(Boolean)
              ));
              return (
                <button key={t.thread_key} onClick={() => selectThread(t)} data-testid={`thread-${t.thread_key}`} className={`w-full text-left px-4 py-3 border-b border-[var(--peak-border)] hover:bg-[var(--peak-surface-alt)] ${active ? "bg-[var(--peak-surface-alt)]" : ""} ${unread > 0 && !active ? "bg-[#FFF9E5]" : ""}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs font-bold uppercase tracking-widest truncate max-w-[65%] ${unread > 0 ? "" : ""}`}>
                      {unread > 0 && <span data-testid={`thread-unread-dot-${t.thread_key}`} className="inline-block w-2 h-2 bg-[var(--peak-accent)] rounded-full mr-2 align-middle" />}
                      {otherName}
                    </span>
                    <span className="text-[10px] font-mono text-[var(--peak-muted)]">{t.last_date ? new Date(t.last_date).toLocaleDateString() : ""}</span>
                  </div>
                  <div className={`text-sm truncate mt-1 flex items-center gap-2 ${unread > 0 ? "font-black text-[var(--peak-ink)]" : "font-semibold"}`}>
                    <span className="truncate">{t.subject}</span>
                    {unread > 0 && (
                      <span data-testid={`thread-unread-badge-${t.thread_key}`} className="shrink-0 text-[10px] font-mono bg-[var(--peak-accent)] text-white px-1.5 py-0.5">{unread}</span>
                    )}
                    {t.message_count > 1 && (
                      <span className="shrink-0 text-[10px] font-mono bg-[var(--peak-ink)] text-white px-1.5 py-0.5" data-testid="thread-count">{t.message_count}</span>
                    )}
                    {t.outbound_count > 0 && (
                      <span className="shrink-0 text-[10px] font-mono bg-[var(--peak-accent)] text-white px-1.5 py-0.5">↩</span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--peak-muted)] truncate">{(t.preview || "").slice(0, 100)}</div>
                  {accounts.length > 1 && acctEmails.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1" data-testid="thread-account-chips">
                      {acctEmails.map((ae) => (
                        <span key={ae} className="text-[9px] font-mono bg-[var(--peak-surface-alt)] border border-[var(--peak-border)] text-[var(--peak-muted)] px-1.5 py-0.5 truncate max-w-[200px]" title={`Received on ${ae}`}>{ae}</span>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <div className="brutalist-card p-6 lg:col-span-2 min-h-[400px]" data-testid="thread-detail">
            {!selectedThread && (
              <div className="text-sm text-[var(--peak-muted)] flex items-center gap-2"><ChevronRight className="w-4 h-4" /> Select a conversation to read</div>
            )}
            {selectedThread && (
              <>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="section-label">{selectedThread.participants.join(", ") || "conversation"}</div>
                    <h2 className="font-heading font-black text-2xl tracking-tighter uppercase mt-1">{selectedThread.subject}</h2>
                    <div className="section-label mt-1">{selectedThread.message_count} message{selectedThread.message_count === 1 ? "" : "s"} · {selectedThread.inbound_count} in / {selectedThread.outbound_count} out</div>
                  </div>
                  {!draft && replyTargetId && (
                    <button
                      onClick={draftReply}
                      disabled={drafting}
                      data-testid="ai-reply-btn"
                      className="bg-[var(--peak-blue-deep)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60"
                    >
                      <Sparkles className="w-3 h-3" /> {drafting ? "Drafting…" : "AI Reply"}
                    </button>
                  )}
                </div>

                <div className="mt-4 border-t border-[var(--peak-border)] pt-4 space-y-4 max-h-[480px] overflow-y-auto tick-scroll pr-2" data-testid="thread-messages">
                  {selectedThread.messages.map((m) => {
                    const isOut = (m.direction || "inbound") === "outbound";
                    return (
                      <div key={m.id} data-testid={`msg-${m.id}`} className={`p-3 border-l-4 ${isOut ? "border-[var(--peak-accent)] bg-[#FFF7F3]" : "border-[var(--peak-blue-deep)] bg-[#F5F7FF]"}`}>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="text-xs font-bold uppercase tracking-widest truncate">
                            {isOut ? "You →" : (m.from_name || m.from_email)}
                            {!isOut && <span className="text-[var(--peak-muted)] ml-1 lowercase font-mono">&lt;{m.from_email}&gt;</span>}
                          </div>
                          <div className="text-[10px] font-mono text-[var(--peak-muted)]">{m.date ? new Date(m.date).toLocaleString() : ""}</div>
                        </div>
                        <div className="text-xs text-[var(--peak-muted)] mt-0.5">to {(m.to || []).join(", ") || "—"}{m.account_email ? <span className="ml-2 font-mono">· via {m.account_email}</span> : null}</div>
                        <div className="mt-2 whitespace-pre-wrap text-sm">{m.text || "(no plaintext content)"}</div>
                      </div>
                    );
                  })}
                </div>

                {draft && (
                  <div className="mt-6 border-t-2 border-[var(--peak-ink)] pt-5" data-testid="ai-reply-panel">
                    <div className="flex items-center justify-between mb-3">
                      <div className="section-label inline-flex items-center gap-1"><Sparkles className="w-3 h-3 text-[var(--peak-blue-deep)]" /> AI Draft {draft.contact_name ? `· enriched with CRM history for ${draft.contact_name}` : "· no CRM record for sender"}</div>
                      <button onClick={() => setDraft(null)} data-testid="discard-draft-btn" className="p-1.5 border border-[var(--peak-border)] hover:border-[#E50000] hover:text-[#E50000]"><X className="w-3.5 h-3.5" /></button>
                    </div>
                    <label className="section-label">To</label>
                    <input className="brutalist-input" type="text" value={draft.to} readOnly data-testid="reply-to" />
                    <label className="section-label mt-3">Subject</label>
                    <input className="brutalist-input" type="text" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} data-testid="reply-subject" />
                    <label className="section-label mt-3">Body</label>
                    <textarea className="brutalist-input min-h-[220px] font-mono text-sm" value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid="reply-body" />
                    <div className="mt-4 flex gap-2 flex-wrap">
                      <button onClick={sendReply} disabled={sending} data-testid="send-reply-btn" className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60">
                        <Send className="w-3 h-3" /> {sending ? "Sending…" : "Send reply"}
                      </button>
                      <button onClick={draftReply} disabled={drafting} data-testid="regenerate-btn" className="border-2 border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase text-xs tracking-widest px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60">
                        <Sparkles className="w-3 h-3" /> {drafting ? "Drafting…" : "Regenerate"}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        </>
      )}

      {/* Compose modal */}
      {composing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="compose-modal" onClick={() => !composeSending && setComposing(false)}>
          <div className="brutalist-card p-6 w-full max-w-2xl bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="section-label">Compose new email</div>
                <h2 className="font-heading font-black text-2xl tracking-tighter uppercase mt-1">New message</h2>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setAiOpen((v) => !v)} data-testid="compose-ai-toggle" className={`inline-flex items-center gap-1 font-bold uppercase text-xs tracking-widest px-3 py-2 border-2 ${aiOpen ? "bg-[var(--peak-blue-deep)] text-white border-[var(--peak-blue-deep)]" : "border-[var(--peak-blue-deep)] text-[var(--peak-blue-deep)] hover:bg-[var(--peak-blue-deep)] hover:text-white"}`}>
                  <Sparkles className="w-3 h-3" /> {aiOpen ? "Close AI" : "Ask AI to draft this"}
                </button>
                <button onClick={() => !composeSending && setComposing(false)} data-testid="compose-close-btn" className="p-1.5 border border-[var(--peak-border)] hover:border-[#E50000] hover:text-[#E50000]"><X className="w-4 h-4" /></button>
              </div>
            </div>

            {aiOpen && (
              <div className="mb-4 p-4 bg-[#F5F7FF] border-2 border-[var(--peak-blue-deep)]" data-testid="ai-draft-panel">
                <div className="section-label inline-flex items-center gap-1 mb-2"><Sparkles className="w-3 h-3 text-[var(--peak-blue-deep)]" /> AI draft</div>
                <label className="section-label">Pick a contact <span className="text-[var(--peak-muted)] normal-case tracking-normal">(optional — leave blank to draft from just an email)</span></label>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--peak-muted)]" />
                  <input
                    className="brutalist-input pl-9" type="text" placeholder="Search by name / email / company…"
                    value={aiSearch}
                    onChange={(e) => { setAiSearch(e.target.value); setAiSelected(null); }}
                    data-testid="ai-contact-search"
                  />
                </div>
                {aiSearch && aiContacts.length > 0 && !aiSelected && (
                  <div className="mt-1 border border-[var(--peak-border)] bg-white max-h-48 overflow-y-auto tick-scroll" data-testid="ai-contact-results">
                    {aiContacts.map((c) => (
                      <button key={c.id} onClick={() => pickContact(c)} data-testid={`ai-contact-${c.id}`} className="w-full text-left px-3 py-2 border-b border-[var(--peak-border)] hover:bg-[var(--peak-surface-alt)]">
                        <div className="font-bold text-sm truncate">{c.first_name} {c.last_name} <span className="text-[var(--peak-muted)] font-mono font-normal">· {c.email}</span></div>
                        {c.company && <div className="text-xs text-[var(--peak-muted)]">{c.company}{c.score != null ? ` · score ${c.score}` : ""}</div>}
                      </button>
                    ))}
                  </div>
                )}
                {aiSelected && (
                  <div className="mt-2 inline-flex items-center gap-2 bg-white border border-[var(--peak-ink)] px-2 py-1 text-xs font-mono" data-testid="ai-contact-selected">
                    <Check className="w-3 h-3 text-[#008A27]" />
                    {aiSelected.first_name} {aiSelected.last_name} · {aiSelected.email}
                    <button onClick={() => { setAiSelected(null); setAiSearch(""); }} className="ml-1 text-[var(--peak-muted)] hover:text-[#E50000]"><X className="w-3 h-3" /></button>
                  </div>
                )}
                <label className="section-label mt-3">Goal (one line)</label>
                <input
                  className="brutalist-input" type="text" placeholder="e.g. book a Tuesday demo"
                  value={aiGoal} onChange={(e) => setAiGoal(e.target.value)}
                  data-testid="ai-goal"
                  onKeyDown={(e) => { if (e.key === "Enter") aiDraft(); }}
                />
                <button
                  onClick={aiDraft}
                  disabled={aiDrafting || !aiGoal.trim()}
                  data-testid="ai-draft-generate"
                  className="mt-3 bg-[var(--peak-blue-deep)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60"
                >
                  <Sparkles className="w-3 h-3" /> {aiDrafting ? "Drafting…" : "Draft with Claude"}
                </button>
              </div>
            )}

            <label className="section-label">From</label>
            <select
              className="brutalist-input"
              value={compose.account_id}
              onChange={(e) => setCompose({ ...compose, account_id: e.target.value })}
              data-testid="compose-from"
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.provider} · {a.email}</option>
              ))}
            </select>
            <label className="section-label mt-3">To</label>
            <input
              className="brutalist-input" type="email" placeholder="lead@example.com"
              value={compose.to_email} onChange={(e) => setCompose({ ...compose, to_email: e.target.value })}
              data-testid="compose-to"
            />
            <label className="section-label mt-3">Subject</label>
            <input
              className="brutalist-input" type="text" placeholder="Quick idea for your team"
              value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })}
              data-testid="compose-subject"
            />
            <label className="section-label mt-3">Body</label>
            <textarea
              className="brutalist-input min-h-[220px] font-mono text-sm"
              placeholder={"Hey there,\n\nWanted to reach out because..."}
              value={compose.body} onChange={(e) => setCompose({ ...compose, body: e.target.value })}
              data-testid="compose-body"
            />
            <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs text-[var(--peak-muted)]">Replies will land back in <span className="font-mono">{accounts.find((a) => a.id === compose.account_id)?.email || "your mailbox"}</span></div>
              <div className="flex gap-2">
                <button onClick={() => setComposing(false)} disabled={composeSending} data-testid="compose-cancel-btn" className="border-2 border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase text-xs tracking-widest px-4 py-2">Cancel</button>
                <button onClick={sendCompose} disabled={composeSending} data-testid="compose-send-btn" className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60">
                  <Send className="w-3 h-3" /> {composeSending ? "Sending…" : "Send"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
